import posthog from "posthog-js";
import { getStoredSessionId } from "@/services/auth.service";
import { getStoredAccessCode } from "@/services/accessCode.service";
import type { DemoUserProfile } from "@/interfaces/demoUser.interface";

export function registerSession() {
  const sessionId = getStoredSessionId();
  if (sessionId) posthog.register({ session_id: sessionId });
}

export function unregisterSession() {
  posthog.unregister("session_id");
}

/**
 * Re-applies the access_code super property on every app load. Without this,
 * only sessions that submit a *fresh* code (see AccessCode.tsx) get tagged —
 * returning visitors whose access was already granted in a prior session
 * never re-run that code path, so their events silently lose the property.
 */
export function registerAccessCode() {
  const code = getStoredAccessCode();
  if (code) posthog.register({ access_code: code });
}

/**
 * Ties all subsequent events to the signed-in demo user and tags them with
 * role/organization so dashboards can break engagement down by segment
 * without touching every individual capture call site. Called on every
 * auth-state resolution (login, signup, and session restore on refresh) so
 * returning visitors stay identified without re-authenticating.
 */
export function identifyDemoUser(profile: DemoUserProfile) {
  posthog.identify(profile.uid, {
    email: profile.email,
    role: profile.role,
    organization: profile.organization,
  });
  posthog.register({ role: profile.role, organization: profile.organization });
}

/** Capture an exception to PostHog. No-op in development. */
export function captureError(error: unknown, additionalProperties: Record<string, unknown> = {}): void {
  if (import.meta.env.MODE === 'development') return;
  posthog.captureException(error, additionalProperties);
}

export default posthog;
